/**
 * 视频创建页表单态类型 — 从 VideoCreatePage 抽出,供创建页 + 其派生 hook(约束 / 积分估算)共享。
 * P2-3 拆解(2026-05-30):页面 1594 行过大,先把跨 hook 共享的类型与派生逻辑收口。
 */
import type {
  GenerationMode,
  VideoEntity,
  VideoResolution,
  VideoShotReference,
} from "../api/videoService";

/** AI 编排会智能填充、但用户手选后必须保留(不被 AI 覆盖)的字段。BGM 走 bgmId!=null 单独判,不在此列。 */
export const AI_OVERRIDABLE_USER_FIELDS = [
  "ratio",
  "voiceTone",
  "voiceEmotion",
  "voiceSpeechRate",
  "voiceVolume",
  "fontStyle",
  "transition",
] as const;

/** 把 patch 中命中可锁字段的 key 并入 lockedFields(去重)。用户改设置即调用,标记"手选过"。 */
export function withUserLocks(prev: string[] | null | undefined, patch: object): string[] {
  const set = new Set(prev ?? []);
  for (const k of Object.keys(patch)) {
    if ((AI_OVERRIDABLE_USER_FIELDS as readonly string[]).includes(k)) set.add(k);
  }
  return [...set];
}

/** 0924 车 B:自由创作素材行三态。 */
export type FreeMaterialMode = "none" | "frame" | "refs";

/** 自由创作参考图一项(uploadFrame 的 {url,key})。 */
export interface FreeRefImage {
  key: string;
  url: string;
}

export interface FormState {
  userInput: string;
  name: string;
  /** 智能选择 model = true 时,提交按素材自动 pick;false 时用 form.provider/model */
  useAutoModel: boolean;
  provider: string;
  model: string;
  generationMode: GenerationMode;
  resolution: VideoResolution;
  ratio: string;
  durationSec: number;
  generateAudio: boolean;
  firstFrameUrl?: string;
  lastFrameUrl?: string;
  negativePrompt?: string;
  styleCode?: string;
  industryCode?: string;
  sourceRef?: Record<string, unknown>;
  source: "FREE" | "TEMPLATE" | "FROM_POSTER";
  // V42:平台 BGM id 或自传 BGM object key,二选一
  bgmId?: number | null;
  customBgmObjectKey?: string | null;
  // ★契约 v2 基准数组(2026-07-16):每镜环境(≤24 字)。**长度定镜数** —— 全页的「有没有分镜 /
  // 几镜」都以它为准,取代已删的 panelPrompts(导演不再产 prompt 文本,编译层是唯一生产者)。
  panelScenes?: string[] | null;
  // 每镜主体动作(≤36 字,一个主动作+表情/细节)。与 panelScenes 并列的第二个窄口 —— 漏了就等于没内容。
  panelMotions?: string[] | null;
  // 每镜主体指代(@token → subjects 注册表);导演产出,UI 不编辑,纯透传。
  panelSubjectRefs?: string[] | null;
  // 每镜首帧 key/url(锚点 i2i 生成或上传)
  panelFirstFrameKeys?: string[] | null;
  panelFirstFrameUrls?: string[] | null;
  // 每镜生成模式 T2V/I2V(导演产出);T2V 镜不出首帧、纯文生视频
  shotMode?: string[] | null;
  anchorKey?: string | null;
  // 自由文案预填 brief(放意图框前,喂 compose 编排剧本/运镜/风格)
  briefStyle?: string | null;
  briefPurpose?: string | null;
  briefDuration?: string | null;
  briefTone?: string | null;
  briefBrand?: string | null;
  // VID-BRIEF-1 选品即带料:从产品库选品后随编排上车的 productBrief。
  // SellingPoints=勾选中的(默认全选,backend brief() 只读它);SellingPointsAll=该品全部卖点
  // (chips 渲染 + 点回来用,草稿往返后产品树不在手上,全集必须随 formJson 存)。
  /**
   * 选品即带料的**真源**(2026-08-19 扩多产品)。下面三个旧键是它的派生投影 ——
   * api 的 VideoDraftFormUtil 读的是那三个,所以它们必须一起更新(见 multiProductBrief)。
   * 老草稿没有本字段,`briefProductsFromLegacy` 从旧键读回。
   */
  briefProducts?: import("./multiProductBrief").BriefProduct[] | null;
  briefProductId?: string | null;
  briefProductName?: string | null;
  briefSellingPoints?: string[] | null;
  briefSellingPointsAll?: string[] | null;
  // 「附品牌事实」开关:知识库审核过的资料随编排注入(后端按 review_status=approved 闸)。
  briefIncludeBrandFacts?: boolean | null;
  // V42.x LLM 一次性产出的结构化字段(单图也用):
  panelDurations?: number[] | null;
  negativePrompts?: string[] | null;
  voiceovers?: string[] | null;
  voiceTone?: string | null;
  voiceEmotion?: string | null;
  voiceEmotions?: string[] | null;
  theme?: string | null;
  brandFocus?: string | null;
  suggestedBgmMood?: string | null;
  fontStyle?: string | null;
  voiceSpeechRate?: number | null;
  voiceVolume?: number | null;
  transition?: string | null;
  subtitleFontScale?: string | null; // small/medium/large
  subtitlePosition?: string | null; // bottom/center/top
  // 用户手选优先(2026-06-02):AI 编排会"智能填充"以下字段,但用户一旦手选就不该被覆盖。
  // 用户改动即把字段名写进 lockedFields;compose 合并(前端 applyBootstrap + 后端 mergeStoryboard)跳过锁定项。
  lockedFields?: string[];
  // 增量 2(2026-05-30「主角锚点」):开启后多镜先出 1 张主角定妆图,各镜首帧 i2i 锚定它锁主角形象。
  useProtagonistAnchor?: boolean;
  // 主角定妆图 key —— 批量出帧时落下,供单镜重生复用(regen 也锚定主角不漂移)。
  protagonistAnchorKey?: string | null;
  // packs W4「视频选人」:选了人物库人物 → 锚=其主定妆图(编排/回填不覆盖,见 characterPick.ts);
  // appearance 快照在编排时逐字拼进 userInput(文本锁),这里只存展示与判定用的三元组。
  characterId?: number | null;
  characterName?: string | null;
  characterAppearance?: string | null;
  characterIdentityKeys?: string[] | null;
  // 0813:是否真人 —— 真人的定妆图送进引擎必被肖像审核拒(见 characterPick.realPersonBlockReason)。
  characterIsRealPerson?: boolean | null;
  /**
   * SEEDANCE 真人出镜 P0(0914 车 D):选人时快照 `CharacterDto.verification.status`
   * (与 appearance/isRealPerson 同为选人时刻快照,人物库后续改动不追溯已建草稿)。
   * 只对真人人物有意义;虚拟人物恒为 null 且 realPersonBlockReason 压根不读它。
   * undefined/null = 未拿到 verification 块(老后端兼容态)—— 按"未验证"处理,不当"免检"。
   */
  characterVerificationStatus?: string | null;
  /**
   * 同上,快照 `CharacterDto.verification.assets`(0914 契约补齐追加)—— 与 status 是
   * 两把尺,见 characterService.ts `CharacterVerificationDto` 头注。status 放行后还要
   * 这个 === "active" 才真的能出镜;null/undefined = 老后端兼容态,按 "none" 处理(拦)。
   */
  characterVerificationAssets?: string | null;
  // 结构化运镜/景别/布光/叙事(导演只填受控枚举,UI 不编辑;bootstrap/draft → createTask 透传
  // 给 ai 编译器/门控)。★契约 v2 已删 lensMm/depthsOfField/colorTemperatures/textureKeywords ——
  // 逐镜自由发挥的入口,风格改由用户选的 styleCode 一次定调。
  shotSizes?: string[] | null;
  cameraMovements?: string[] | null;
  cameraSpeeds?: string[] | null;
  lightingKeys?: string[] | null;
  shotIntents?: string[] | null;
  narrativeRoles?: string[] | null;
  heroMoments?: boolean[] | null;
  // 批1 连续性:per-shot 场景标签/衔接声明 + 主体注册表(导演产出,UI 不编辑,纯透传)
  sceneIds?: string[] | null;
  shotContinuities?: string[] | null;
  subjects?: string[] | null;
  /** 批2 hook 三轨落档(storyboard 级;提交透传给 ai 合成层)。 */
  hookPlan?: Record<string, unknown> | null;
  /** item1 事实→画面(per-shot;compose 回传并入 form → createTask 透传给 ai 编译器)。 */
  factRefs?: string[][] | null;
  factVisuals?: string[] | null;
  /** T2A 上传图实体表 + 单产品图便捷位(compose 回传实体;确认单可纠;createTask 透传给模式路由器)。 */
  entities?: VideoEntity[] | null;
  productRefKey?: string | null;
  /**
   * 0924 车 B:自由创作「素材」行三选一(none=不用 / frame=上传首帧(可再传尾帧)/ refs=参考图 ≤4)。
   * 只有自由创作页会写它;组包时由 `freeMaterial.freeMaterialRequest` 翻成契约 §3.3 的字段。
   * ★首帧/尾帧与参考图**互斥**(上游 400)——切换模式时另一侧整块清空,见 `switchFreeMaterialMode`。
   */
  freeMaterialMode?: FreeMaterialMode | null;
  /** 自由创作上传首帧的稳定 key(uploadFrame 返回;firstFrameUrl 是会过期的签名,retry 靠 key 现签)。 */
  firstFrameKey?: string | null;
  /** 自由创作上传尾帧的稳定 key(→ 请求 `lastFrameKey`,理由同上)。 */
  lastFrameKey?: string | null;
  /** 自由创作参考图(产品/物件/场景,≤4);url 只供缩略图,上送只用 key(`key://` 形态)。 */
  freeRefImages?: FreeRefImage[] | null;
  /** W3:逐镜口播三态(voiceover|dialogue|ambient)+ 参考派活(per-shot;compose 回传并入
   *  form → createTask 透传;确认单三态徽标/逐镜参考摘要读这里)。 */
  speechModes?: string[] | null;
  references?: VideoShotReference[][] | null;
  // ── 批 B2 台本预览图(2026-08-05)——**与首帧分开的字段**,理由见 ai/api 侧注释:
  //    panelFirstFrame* 是**渲染输入**(有帧 → 整个任务翻 I2V + 段界二选一撞 Ark 400),
  //    预览图只是给人看的,混进去 = 静默改变出片方式。
  panelPreviewKeys?: string[] | null;
  panelPreviewUrls?: string[] | null;
  /**
   * 每镜都出台本预览图。**只认显式 `true`**;`false` / `null` / 缺省一律 = 只给真要当起始画面的镜出图。
   *
   * ★2026-08-09 默认反转(此前是「缺省/true = 每镜都出」):45s 单每镜都出 = 15 张 × 700 =
   * 10,500 积分,用户原话「不需要出那么多无意义的图」。三态收成两态是有意的 ——
   * 老草稿里这个字段是 undefined(用户从没碰过那个勾),若还按「缺省 = 每镜都出」读,
   * 重开老草稿就会**勾没勾上却烧满图**,界面比实际乐观。api `generateFrames` 同批同改。
   */
  previewFrames?: boolean | null;
  // 故事引擎(2026-05-31):讲成一个完整小故事(默认开);开时自动锁主角。
  storyMode: boolean;
  // 爆款蓝图预载(P-A 视频闭环):从 /ai-explosive「去做视频」带入;随 formJson 落草稿,
  // backend brief() 把 formulaPayload 透传 ai compose → 架构师按蓝图结构出故事(结构真进渲染链)。
  explosiveBlueprintId?: number | null;
  explosiveFormulaPayload?: string | null; // 蓝图 payload 的 JSON 串
  explosiveFormulaName?: string | null; // 套路名(蓝图条展示;不参与编排)
  // M2 复刻档(设计 §4 两档):learn=学套路(默认)/ copy=仿拍(须蓝图有 sceneCards);
  // backend brief() 透传 ai ComposeRequest.fidelityMode。
  explosiveFidelityMode?: "learn" | "copy" | null;
  // 来源平台(蓝图条「来自 抖音」展示;随 formJson 存,草稿重开不丢)。
  explosiveSourcePlatform?: string | null;
  /**
   * 这条蓝图留了原片没有(EXP-RAW-1,2026-09-02)—— 仿拍档的文案据此分叉:
   * 有原片 = 参考原片**画面**仿拍(r2v);没有 = 维持现状,照场景卡文字重新想象画面。
   * 与 sourcePlatform 同性质:来自路由交接、随 formJson 存草稿、纯展示不参与提交。
   * 存量草稿恒 undefined → 按「没有」渲染(诚实降级,不许乐观)。
   */
  explosiveHasRawVideo?: boolean | null;
  /**
   * 「参考片在拍什么」(2026-08-14)—— 逐拍画面 + 该拍口播 + 记忆点,人读的一段文本。
   *
   * ★用途一:备料页展示,让用户看见我们**真的看懂了这条爆款**(老板拍板)。
   * ★用途二:随请求走**独立通道**给引擎,让它按参考片的拍法与内容组织方式复刻。
   * ★绝不并进 `userInput`:那个框是「用户自己的产品事实」,混进参考片内容会被
   *   `classify_input` 判成用户卖点,引擎随即被命令逐条落进口播 —— 成片讲参考片的产品
   *   (0812「带过去都是节奏」同一机制,A/B 28/52 分 vs 拿掉后 65 分)。
   */
  explosiveReferenceContent?: string | null;
  // M2 拍→镜映射(ai 仿拍响应加性字段;学套路为空):每镜所属节拍名 → 结构轨聚合+逐镜挂拍标签。
  panelBeats?: string[] | null;
  /**
   * 波2「选拍法」——**用户点的**那张卡 key(`formulas.yaml`);null/缺省 = 引擎自动(现状)。
   * backend `brief()` 把它映射成 ai `ComposeRequest.formulaKey`。
   */
  pickedFormulaKey?: string | null;
  /** 同上,人读的拍法名(来源条 / 详情页显示用;存下来省得那两处各自再拉一次 taxonomy)。 */
  pickedFormulaName?: string | null;
  /**
   * 调性档(0924 车 C):**当前生效的档**。用户没点过 chip 时由创建页按租户行业写默认值
   * (`model/toneRegister.ts`,与 ai 同表);点过 → `toneRegisterTouched=true`,不再跟行业。
   * 随草稿 formJson 进 compose(导演在编排阶段就读档位)、随 buildTaskRequest 进建任务。
   */
  toneRegister?: import("./toneRegister").ToneRegister | null;
  /** 用户是否手点过调性 chip(true 后行业默认不再覆盖)。 */
  toneRegisterTouched?: boolean;
  /**
   * 编排响应顶层回的画面大字档(0924 车 C,{mode,reason[,cappedFrom]})—— **只读展示**。
   * 渲染真正读的是 `hookPlan.textOverlay`(随 hookPlan 原样透传),console 不改写任何一份。
   */
  textOverlay?: import("./toneRegister").TextOverlayInfo | null;
  /**
   * ★下面两个是**引擎实际用了哪张**(compose 响应 → backend mergeStoryboard 写回 formJson),
   * 与上面那个用户选择**分开存**,不是重复字段:
   *   - 引擎没套用时(贴的是分镜脚本 → 忠实拆解不选套路)响应里是 null,merge 跳过,
   *     这两个字段保持空 —— 于是详情页「本次设置」如实显示「未套用」;
   *   - 若合并成一个字段,那种情况下留在 formJson 里的是**用户选的那张**,
   *     详情页就会显示一张根本没用上的拍法(「概览比被概览的更乐观」,codex 抓过两次)。
   */
  formulaKey?: string | null;
  formulaName?: string | null;
  /**
   * ★「这次实际按什么目的/多长编排,以及**是谁定的**」(2026-08-02,ai `intent_infer` 产)。
   *
   * 与 `briefPurpose`(用户选的)**分开存**,理由与上面 formulaKey/pickedFormulaKey 那对逐字相同:
   * 合成一个字段的话,「用户没选、AI 推的」与「用户选的」就再也分不开,界面会把 AI 的推断
   * 显示成用户的选择 —— 那正是波2 修掉的那个错。
   *
   * `purposeSource` 三值与 ai 侧 `ResolvedIntent.purpose_source` 逐字同源:
   *   user(用户选的)/ inferred(从文案推的)/ fallback(没推出来,走兜底档)。
   * 措辞由 `purposeNotice()` 单点决定 —— 三态说法不同,混了就是撒谎(见该模块 docstring)。
   *
   * 到达路径:ai compose 响应 → backend `mergeStoryboard`(白名单外全落,**api 零改动**)
   * → formJson → 这里。老草稿全缺,`purposeNotice` 按 pending 处理、不假装。
   */
  resolvedPurpose?: string | null;
  purposeSource?: string | null;
  purposeReason?: string | null;
  resolvedDurationSec?: number | null;
  durationSource?: string | null;
  durationReason?: string | null;
  /**
   * ★AI 反对意见(2026-08-10,VID-CMP-06):用户**手选**的档与文案信号分歧时,引擎本会判的
   * 那一档 + 依据;无分歧或非手选时为空。到达路径同上(compose → mergeStoryboard → formJson)。
   * 运行时它早随 `{...parsed}` 展开进了 form,但类型上不可见 —— 任何按字段名挑拣/重建 form
   * 的代码都会把它静默丢掉,编译器毫无知觉;`purposeNotice` 的 user 分支拿不到 hint,
   * 分歧留痕整条断线。补类型 = 把已经在跑的数据接回编译期。
   */
  purposeHint?: string | null;
  purposeHintReason?: string | null;
  // 架构师产出的故事圣经(供「故事大纲」折叠展示);compose 回传。
  // ★W4(2026-09-13):补 architecture_type/persuasion——导演交代卡的架构 chip + 论证弧
  // 取的就是这两个子字段;老草稿/旧 ai 都没有,UI 侧按缺席不显处理(VideoDirectorBriefCard)。
  storyBible?: {
    logline?: string;
    protagonist?: { name?: string; appearance?: string; persona?: string };
    beats?: string[];
    architecture_type?: "persuasion" | "experience" | "story";
    persuasion?: { argument_arc?: { stage?: string; content?: string }[] };
  } | null;

  // ═══ W4(2026-09-13)幕二分镜工作台:新导演管线产出,compose 回传 → 并入 form ═══
  // ★老草稿/旧 ai 响应都没有这些字段;新 UI 全部按「缺席则整块不渲染」处理。
  /** 选中的卖点 fact 码(如 ["F1.1","F1.2"])。 */
  selectedFacts?: string[] | null;
  /** 这次没讲的卖点 fact 码 —— 「这次这 N 条不讲」灰 chip 区取它。 */
  unusedFacts?: string[] | null;
  /** 引擎按内容估的成片总秒数(与用户选的时长档 briefDuration 分开存,语义见「AI 按内容定」)。 */
  estimatedDurationSec?: number | null;
  /** F 码 → 卖点原文(选料清单 / 逐镜 fact chip 取文的唯一源;缺映射时 UI 退回显示 fid 本身)。 */
  factTexts?: Record<string, string> | null;
  /** 审片人打分(0-100 或引擎自定档,UI 只透传显示)。 */
  reviewScore?: number | null;
  /** 审片人一段话意见,原文照登(可能含负面措辞,不许精修)。 */
  reviewVerdict?: string | null;
  /**
   * 1013 V5:审片人缺料反问 —— 只有用户知道的事实(人名头衔/报名方式/价格…),导演不许编。
   * q=问用户的问题,why=缺它哪镜不成立,slot=建议填到哪(引擎自定,UI 不依赖)。
   */
  reviewMissingInputs?: { q?: string | null; why?: string | null; slot?: string | null }[] | null;
  /**
   * 「换进去」产生的钉选料(用户输入,不是编排结果)——下一次编排按它选料,
   * 不进 STORYBOARD_BACKUP_KEYS(与 briefStyle 等编排输入同族,不随「恢复上一版分镜」还原)。
   */
  pinnedFactIds?: string[] | null;
}
